import {isEqual} from "lodash";

import {
    CopilotChatStoreGetter,
    CopilotChatStoreSetter,
} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";

/**
 * Updates the user context in the copilot chat store.
 * Only updates properties if their values have actually changed.
 * This ensures that when arrays become empty (like when nothing is selected),
 * they're properly reflected in the state without stale data persisting.
 * This approach prevents infinite update loops by avoiding unnecessary state updates.
 *
 * Handles both existing and new fields, with special handling for arrays to ensure
 * they're properly replaced rather than merged.
 */
export const updatedUserContext = (set: CopilotChatStoreSetter, get: CopilotChatStoreGetter) => {
    return (userContext: Partial<ReturnType<typeof get>["userContext"]> & Record<string, unknown>) => {
        const currentUserContext = get().userContext;
        let hasChanges = false;

        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(userContext).forEach(([key, value]) => {
            if (key in currentUserContext) {
                if (!isEqual(currentUserContext[key as keyof typeof currentUserContext], value)) {
                    hasChanges = true;
                }
            } else {
                hasChanges = true;
            }
        });

        if (hasChanges) {
            set((draft) => {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                Object.entries(userContext).forEach(([key, value]) => {
                    (draft.userContext as Record<string, unknown>)[key] = value;
                });
            });
        }
    };
};
